import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import clsx from 'clsx';
import { Card as Card$1 } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { forwardRef, useState, useRef, useEffect, useImperativeHandle } from 'react';
import { truncateString } from '../../_utils/truncate-string.js';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { LoadingBlur } from '../LoadingBlur/LoadingBlur.js';
import { Menu } from '../Menu/Menu.js';

function CardComponent(props, ref) {
  var _a;
  const {
    cardClassName,
    className,
    title: titleProps,
    titleHint,
    actions,
    children,
    defaultFolded,
    size,
    severity,
    header,
    onClick,
    contrast,
    menuProps,
    subTitle,
    nativeHeader,
    small,
    pt,
    dropShadow,
    showCollapseButton,
    loading,
    wrapperClassName,
    onExpandedChange
  } = props;
  const [isExpanded, setIsExpanded] = useState(!defaultFolded);
  const canFold = (_a = props.foldable) !== null && _a !== void 0 ? _a : true;
  const onExpandedChangeRef = useRef(onExpandedChange);
  onExpandedChangeRef.current = onExpandedChange;
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    var _a;
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    (_a = onExpandedChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onExpandedChangeRef, isExpanded);
  }, [isExpanded]);
  useImperativeHandle(ref, () => ({
    setIsExpanded
  }));
  const title = typeof titleProps === 'string' ? truncateString(titleProps, 50) : titleProps;
  const cardClass = clsx(small ? 'small' : null, !isExpanded ? 'content-hidden' : null, cardClassName, size, severity, header ? 'has-custom-header' : null, contrast ? 'contrast' : null, !children ? 'empty-body' : null, dropShadow ? 'drop-shadow' : null, onClick ? 'clickable' : null, 'h-full');
  const contentClass = clsx('h-full', className);
  const cardHeaderClass = clsx('card-custom-header', severity);
  const collapsibleButtonClass = clsx(isExpanded ? 'expanded' : null);
  const actionsNode = actions ? Array.isArray(actions) ? jsx(Menu, Object.assign({}, menuProps, {
    items: actions
  })) : actions : null;
  const textCardTitle = jsxs("div", {
    className: "flex flex-row justify-content-between align-items-center",
    children: [jsxs("div", {
      className: "flex flex-row gap-2",
      children: [jsx("div", {
        className: "p-card-title-header",
        children: title
      }), titleHint ? jsxs(Fragment, {
        children: [jsx(Tooltip, {
          target: "pi-question-circle"
        }), jsx("i", {
          className: "pi pi-question-circle",
          "data-pr-tooltip": titleHint
        })]
      }) : null]
    }), jsxs("div", {
      className: "flex gap-4",
      children: [actionsNode, !actions && canFold || showCollapseButton ? jsx(Button, {
        className: collapsibleButtonClass,
        tertiary: true,
        outlined: true,
        icon: jsx(Icon, {
          name: "keyboard_arrow_down"
        }),
        label: isExpanded ? 'Hide' : 'Show',
        iconPos: "right",
        onClick: () => {
          setIsExpanded(!isExpanded);
        }
      }) : null]
    })]
  });
  const templateCardTitle = typeof title !== 'string' ? title : textCardTitle;
  const cardTitle = Boolean(title) ? templateCardTitle !== null && templateCardTitle !== void 0 ? templateCardTitle : textCardTitle : null;
  const cardHeader = header ? jsx("div", {
    className: cardHeaderClass,
    children: header
  }) : null;
  const wrapperClass = clsx('w-full relative', wrapperClassName);
  return jsx("div", {
    className: wrapperClass,
    children: jsxs(LoadingBlur, {
      className: "border-round-xl",
      loading: loading,
      children: [cardHeader, jsx(Card$1, {
        onClick: onClick,
        className: cardClass,
        title: cardTitle,
        subTitle: subTitle,
        header: nativeHeader,
        pt: pt,
        children: className ? jsx("div", {
          className: contentClass,
          children: children
        }) : children
      })]
    })
  });
}
const Card = /*#__PURE__*/forwardRef(CardComponent);

export { Card };
